import { cn } from '@/shared/lib/utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva } from 'class-variance-authority';
import * as React from 'react';

/**
 * Figma 라이브러리 Tab (440:1258) · Tab (Item) (440:1195)
 *
 * variant (Type)
 * - square: 밑줄형. 항목 높이 48 · 좌우 24, 선택 = 아래 2px 검정 + SemiBold (GNB · 상세 탭)
 * - smallSquare: 회색 바탕 안의 세그먼트. 선택 = 흰 칩 + 그림자 (거래처 목록 거래 중/신청/종료)
 * - round: 알약형. 선택 = 검정 채움 + 흰 글자
 *
 * 링크로 탭을 만들 때(GNB 등)는 tabListVariants · tabItemVariants를 그대로 쓴다.
 * 항목 상태: data-[state=active] (Radix) 또는 data-[status=active] (TanStack Link) · disabled · aria-disabled
 */
export type TabsVariant = 'square' | 'smallSquare' | 'round';

const tabListVariants = cva('flex whitespace-nowrap', {
  variants: {
    variant: {
      // 목록 전체 아래 1px 선 — 선택 항목의 2px 선이 그 위를 덮는다
      square: 'overflow-x-auto shadow-[inset_0_-1px_0_var(--color-line-grey)]',
      smallSquare: 'items-center gap-1 rounded-12 bg-grey-50 p-1',
      round: 'items-center overflow-x-auto',
    },
  },
  defaultVariants: { variant: 'square' },
});

const tabItemVariants = cva(
  'inline-flex shrink-0 items-center justify-center outline-none transition-colors disabled:pointer-events-none aria-disabled:cursor-default',
  {
    variants: {
      variant: {
        square: [
          // 기본 항목도 아래 1px line-grey (Figma Tab Item Square Default), 선택은 2px 검정
          'h-12 border-b border-line-grey bg-surface-white p-4 text-button-14-m text-fg-tertiary [&>*]:px-2',
          'hover:bg-grey-100 focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]',
          'data-[state=active]:border-b-2 data-[state=active]:border-grey-900 data-[state=active]:text-button-14-sb data-[state=active]:text-fg-primary data-[state=active]:hover:bg-surface-white',
          'data-[status=active]:border-b-2 data-[status=active]:border-grey-900 data-[status=active]:text-button-14-sb data-[status=active]:text-fg-primary data-[status=active]:hover:bg-surface-white',
          'disabled:text-fg-disabled aria-disabled:text-fg-disabled aria-disabled:hover:bg-surface-white',
        ],
        smallSquare: [
          'h-7 flex-1 rounded-8 px-3 py-1.5 text-button-14-m text-fg-tertiary',
          'hover:text-fg-secondary focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]',
          'data-[state=active]:bg-surface-white data-[state=active]:text-button-14-sb data-[state=active]:text-fg-primary data-[state=active]:shadow-100',
          'data-[status=active]:bg-surface-white data-[status=active]:text-button-14-sb data-[status=active]:text-fg-primary data-[status=active]:shadow-100',
          'disabled:text-fg-disabled aria-disabled:text-fg-disabled',
        ],
        round: [
          'h-10 rounded-20 px-6 py-3 text-button-14-sb text-fg-primary',
          'hover:bg-grey-100 focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]',
          'data-[state=active]:bg-grey-900 data-[state=active]:text-fg-white',
          'data-[status=active]:bg-grey-900 data-[status=active]:text-fg-white',
          'disabled:text-fg-disabled aria-disabled:text-fg-disabled',
        ],
      },
    },
    defaultVariants: { variant: 'square' },
  },
);
const TabsVariantContext = React.createContext<TabsVariant>('square');

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: TabsVariant;
  }
>(({ className, variant = 'square', ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabListVariants({ variant }), className)}
      {...props}
    />
  </TabsVariantContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabItemVariants({ variant }), className)}
      {...props}
    >
      {/* square 탭은 Figma Text 프레임(좌우 8)을 둔다 */}
      {variant === 'square' ? <span>{children}</span> : children}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = TabsPrimitive.Content;

/* eslint-disable react-refresh/only-export-components -- cva 변형을 GNB 링크 탭에서도 쓴다 */
export {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  tabItemVariants,
  tabListVariants,
};
